import React, { useEffect, useRef, useState } from "react";
import {
  Badge,
  createStyles,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Autocomplete } from "@material-ui/lab";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import TuneIcon from "@material-ui/icons/Tune";
import moment from "moment";

import CardCommon from "../../../../components/card/CardCommon";
import DateRangePicker from "../../../../components/common/DateRangePicker";
import MultipleSelector from "../../../../components/common/selector/MultipleSelector";
import TextfieldCommon from "../../../../components/textField/TextfieldCommon";
import { CustomTheme } from "../../../../types/customTheme";
import {
  channelSelectorList,
  defaultModeSelectorList,
  platformSelectorList,
} from "../../../../utils/consts/list";
import {
  countActiveFilters,
  HistoricalFilterValues,
  SelectorOption,
  TIME_OPTIONS,
} from "./historicalUtils";

/**
 * INSIGHT-HIST-12 — the shared controls for every historical dashboard.
 *
 * Built from the portal's own filter parts so these pages behave like the
 * sales reports: a Filters button carrying a count, and the controls in one
 * card underneath it — the same DateRangePicker and the same chip selectors,
 * with their shared option lists, so "delivery" means the same thing here as
 * it does there.
 *
 * The one addition is a time window. The sales filters work in whole days;
 * a kitchen is judged by its lunch and its evening, so the range picker is
 * paired with a from/to time.
 */

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    // The portal's own Filters button: a filled pill that stands off the page
    // behind it, so it reads as a control rather than a label.
    iconButton: {
      "&:hover": {
        background: "none",
        backgroundColor: theme.palette.background.paper,
        border: `2px solid ${theme.palette.background.entity_border}`,
      },
      backgroundColor: theme.palette.background.entity_highlight_background,
      color: theme.palette.custom.orange.contrastText,
      marginBottom: "8px",
      display: "flex",
      alignItems: "center",
      borderRadius: "10px",
      border: `2px solid ${theme.palette.background.entity_border}`,
      textAlign: "center",
      width: "120px",
      height: "44px",
    },
    badgeFontColor: {
      "& .MuiBadge-badge": {
        color: "white",
      },
    },
    label: {
      color: theme.palette.custom.orange.contrastText,
      fontWeight: "bold",
    },
    /**
     * One height for every control in the card.
     *
     * The parts come from different places — the date button is 60px tall,
     * the chip selector adds its own padding and a small Autocomplete is
     * about 40px — so a row of them lands at three different heights and the
     * card reads as a pile rather than a row. These rules put every input on
     * the date button's 60px and strip the selector's own inset so the left
     * edges line up too.
     */
    control: {
      marginTop: "12px",
      "& .MuiAutocomplete-root": {
        paddingLeft: 0,
        paddingRight: 0,
        marginTop: "6px",
      },
      "& .MuiInputBase-root": {
        minHeight: "60px",
        borderRadius: 10,
      },
    },
    // The same treatment the reset-hour selector uses, so the time dropdowns
    // sit beside the chip selectors without looking borrowed.
    autoComplete: {
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_highlight_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: "none",
        color: theme.palette.custom.orange.contrastText,
      },
    },
  }),
);

// How long to wait after the last key press before a text filter is applied.
const TEXT_FILTER_DELAY_MS = 500;

interface DebouncedTextFilterProps {
  id: string;
  name: string;
  value: string;
  disabled?: boolean;
  onCommit: (value: string) => void;
}

/**
 * A text filter that only reports its value once typing pauses (or on
 * Enter), so the reports aren't re-run against BigQuery on every key press.
 */
const DebouncedTextFilter: React.FunctionComponent<DebouncedTextFilterProps> = ({
  id,
  name,
  value,
  disabled,
  onCommit,
}) => {
  const [text, setText] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Follow changes made from outside, e.g. a station picked in the table.
  // A pending timer from earlier typing is dropped so it can't overwrite it.
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setText(value);
  }, [value]);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const commit = (next: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (next !== value) {
      onCommit(next);
    }
  };

  const handleChange = (event: any) => {
    const next = event.target.value;
    setText(next);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => commit(next), TEXT_FILTER_DELAY_MS);
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter") {
      commit(text);
    }
  };

  return (
    <TextfieldCommon
      id={id}
      name={name}
      type="text"
      value={text}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      disabled={disabled}
    />
  );
};

export interface HistoricalFiltersProps {
  values: HistoricalFilterValues;
  onChange: (values: HistoricalFilterValues) => void;
  /** Station is only worth showing on the station screen. */
  showStation?: boolean;
  /** Driver is only worth showing on the delivery screen. */
  showDriver?: boolean;
  disabled?: boolean;
}

const HistoricalFilters: React.FunctionComponent<HistoricalFiltersProps> = ({
  values,
  onChange,
  showStation = false,
  showDriver = false,
  disabled = false,
}) => {
  const classes = useStyles();
  // Closed on arrival: the numbers are what the page is for, and the defaults
  // already answer the common question. Filters open when asked for.
  const [isOpen, setIsOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const set = (key: keyof HistoricalFilterValues, value: any) =>
    onChange({ ...values, [key]: value });

  // A debounced text filter reports after a delay, so it merges into the
  // latest values rather than the ones from when typing started.
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const setLatest = (key: keyof HistoricalFilterValues, value: any) =>
    onChange({ ...valuesRef.current, [key]: value });

  /**
   * react-date-range hands back one selection; the two dates and the
   * calendar's own state are updated together so they cannot disagree.
   */
  const handleChangeDateRange = (range: any) => {
    onChange({
      ...values,
      startDate: moment(range.selection.startDate).format("YYYY-MM-DD"),
      endDate: moment(range.selection.endDate).format("YYYY-MM-DD"),
      dateSelectionRange: [range.selection],
    });
  };

  /** MultipleSelector reports by key; each chip list is its own field. */
  const handleAllSelectedList = (key: string, selected: SelectorOption[]) => {
    if (key === "mode") {
      set("modeList", selected);
    } else if (key === "platform") {
      set("platformList", selected);
    } else if (key === "channel") {
      set("channelList", selected);
    }
  };

  const activeCount = countActiveFilters(values);

  /**
   * Hours to pick from rather than a free time field: a report is read by the
   * hour, and a list cannot be typed wrong. Clearing it drops the window.
   */
  const timeField = (key: "fromTime" | "toTime", label: string) => (
    <Grid item md={3} sm={6} xs={12} className={classes.control}>
      <Typography variant="body2" className={classes.label}>
        {label}
      </Typography>
      <Autocomplete
        options={TIME_OPTIONS}
        value={values[key] || null}
        onChange={(event: any, option: any) => set(key, option || "")}
        getOptionLabel={(option: any) => option}
        disabled={disabled}
        size="small"
        popupIcon={<ArrowDropDownIcon />}
        renderInput={(params) => <TextField {...params} variant="outlined" />}
        classes={{ root: classes.autoComplete }}
      />
    </Grid>
  );

  const textField = (
    key: "stationId" | "driverId",
    label: string,
  ) => (
    <Grid item md={3} sm={6} xs={12} className={classes.control}>
      <Typography variant="body2" className={classes.label}>
        {label}
      </Typography>
      <DebouncedTextFilter
        id={`historical-filter-${key}`}
        name={key}
        value={values[key] || ""}
        onCommit={(next) => setLatest(key, next)}
        disabled={disabled}
      />
    </Grid>
  );

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "start",
          marginTop: "16px",
        }}
      >
        <Badge
          badgeContent={activeCount}
          color="secondary"
          className={classes.badgeFontColor}
        >
          <IconButton
            className={classes.iconButton}
            onClick={() => setIsOpen(!isOpen)}
          >
            <Typography style={{ display: "flex", alignItems: "center" }}>
              <TuneIcon fontSize="small" />
            </Typography>
            <Typography style={{ marginLeft: "8px", letterSpacing: "1px" }}>
              Filters
            </Typography>
          </IconButton>
        </Badge>
      </div>

      {isOpen && (
        <div style={{ marginBottom: "24px" }}>
          <CardCommon backgroundColor={"entity_background"}>
            <Grid
              container
              spacing={2}
              style={{
                paddingLeft: "20px",
                paddingRight: "20px",
                paddingBottom: "16px",
              }}
            >
              <Grid item md={3} sm={6} xs={12} className={classes.control}>
                <Typography variant="body2" className={classes.label}>
                  Date range
                </Typography>
                <DateRangePicker
                  open={isCalendarOpen}
                  setOpen={setIsCalendarOpen}
                  startDate={values.startDate}
                  endDate={values.endDate}
                  handleChangeDateRange={handleChangeDateRange}
                  dateSelectionRange={values.dateSelectionRange}
                  horizontalMaxWidth={false}
                />
              </Grid>

              {timeField("fromTime", "From time")}
              {timeField("toTime", "To time")}

              <Grid item md={3} sm={6} xs={12} className={classes.control}>
                <Typography variant="body2" className={classes.label}>
                  Mode
                </Typography>
                <MultipleSelector
                  selectorTextFieldName={"mode"}
                  selectorList={defaultModeSelectorList}
                  handleAllSelectedList={handleAllSelectedList}
                  selectedList={values.modeList}
                  disabled={disabled}
                />
              </Grid>

              <Grid item md={3} sm={6} xs={12} className={classes.control}>
                <Typography variant="body2" className={classes.label}>
                  Platform
                </Typography>
                <MultipleSelector
                  selectorTextFieldName={"platform"}
                  selectorList={platformSelectorList}
                  handleAllSelectedList={handleAllSelectedList}
                  selectedList={values.platformList}
                  disabled={disabled}
                />
              </Grid>

              <Grid item md={3} sm={6} xs={12} className={classes.control}>
                <Typography variant="body2" className={classes.label}>
                  Channel
                </Typography>
                <MultipleSelector
                  selectorTextFieldName={"channel"}
                  selectorList={channelSelectorList}
                  handleAllSelectedList={handleAllSelectedList}
                  selectedList={values.channelList}
                  disabled={disabled}
                />
              </Grid>

              {showStation && textField("stationId", "Station")}
              {showDriver && textField("driverId", "Driver")}
            </Grid>
          </CardCommon>
        </div>
      )}
    </>
  );
};

export default HistoricalFilters;
